import { addMonths, compareIso, daysInMonth, firstOfMonth, yearOf } from "./dates";
import { compareTransactions } from "./pdfParse";
import type { Transaction } from "./types";

function appendYearlyPayments(
  future: Transaction[],
  startAfter: string,
  endDate: string,
  amount: number,
  month: number,
  day: number,
  text: string,
): void {
  if (!amount) return;
  const startYear = yearOf(startAfter);
  const endYear = yearOf(endDate);
  for (let year = startYear; year <= endYear; year++) {
    const payDay = Math.min(day, daysInMonth(year, month));
    const d = `${year}-${String(month).padStart(2, "0")}-${String(payDay).padStart(2, "0")}`;
    if (compareIso(startAfter, d) < 0 && compareIso(d, endDate) <= 0) {
      future.push({
        booking_date: d,
        value_date: d,
        text,
        amount,
        source: "projection",
      });
    }
  }
}

export function generateFutureTransactions(
  startAfter: string,
  endDate: string,
  opts: {
    monthly_amount: number;
    monthly_day: number;
    yearly_payment_1: number;
    yearly_month_1: number;
    yearly_day_1: number;
    yearly_payment_2: number;
    yearly_month_2: number;
    yearly_day_2: number;
    annual_fee: number;
    annual_fee_month: number;
    annual_fee_day: number;
  },
): Transaction[] {
  const future: Transaction[] = [];
  const {
    monthly_amount,
    monthly_day,
    yearly_payment_1,
    yearly_month_1,
    yearly_day_1,
    yearly_payment_2,
    yearly_month_2,
    yearly_day_2,
    annual_fee,
    annual_fee_month,
    annual_fee_day,
  } = opts;

  if (monthly_amount) {
    let candidate = firstOfMonth(startAfter);
    while (compareIso(candidate, startAfter) <= 0) {
      candidate = addMonths(candidate, 1);
    }
    while (compareIso(candidate, endDate) <= 0) {
      const [y, m] = candidate.split("-").map(Number);
      const payDay = Math.min(monthly_day, daysInMonth(y, m));
      const payDate = `${y}-${String(m).padStart(2, "0")}-${String(payDay).padStart(2, "0")}`;
      if (compareIso(startAfter, payDate) < 0 && compareIso(payDate, endDate) <= 0) {
        future.push({
          booking_date: payDate,
          value_date: payDate,
          text: "Projected monthly payment",
          amount: monthly_amount,
          source: "projection",
        });
      }
      candidate = addMonths(candidate, 1);
    }
  }

  appendYearlyPayments(
    future,
    startAfter,
    endDate,
    yearly_payment_1,
    yearly_month_1,
    yearly_day_1,
    "Projected yearly payment 1",
  );
  appendYearlyPayments(
    future,
    startAfter,
    endDate,
    yearly_payment_2,
    yearly_month_2,
    yearly_day_2,
    "Projected yearly payment 2",
  );

  if (annual_fee) {
    const feeAmount = -Math.abs(annual_fee);
    const startYear = yearOf(startAfter);
    const endYear = yearOf(endDate);
    for (let year = startYear; year <= endYear; year++) {
      const day = Math.min(annual_fee_day, daysInMonth(year, annual_fee_month));
      const d = `${year}-${String(annual_fee_month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      if (compareIso(startAfter, d) < 0 && compareIso(d, endDate) <= 0) {
        future.push({
          booking_date: d,
          value_date: d,
          text: "Projected annual fee",
          amount: feeAmount,
          source: "projection",
        });
      }
    }
  }

  return future.sort(compareTransactions);
}

export function payNowTransaction(
  amount: number,
  valueDate: string,
): Transaction | null {
  if (!amount) return null;
  return {
    booking_date: valueDate,
    value_date: valueDate,
    text: "One-time payment",
    amount: Number(amount),
    source: "pay_now",
  };
}

export function applyPayNowGuards(
  payNow: Transaction | null,
  valueDate: string,
  earliest: string,
  projectionEnd: string,
): { tx: Transaction | null; warnings: string[] } {
  const warnings: string[] = [];
  if (!payNow) return { tx: null, warnings };

  if (compareIso(valueDate, projectionEnd) > 0) {
    warnings.push(
      `One-time payment date (${valueDate}) is after the projection end (${projectionEnd}); the amount is not applied.`,
    );
    return { tx: null, warnings };
  }
  if (compareIso(valueDate, earliest) < 0) {
    warnings.push(
      `One-time payment date (${valueDate}) is before the first transaction value date (${earliest}); the amount is not applied.`,
    );
    return { tx: null, warnings };
  }
  return { tx: payNow, warnings };
}

export function sortTransactions(txs: Transaction[]): Transaction[] {
  return [...txs].sort(compareTransactions);
}
